import React from 'react';
import { cn } from '../../lib/utils';

/* SectionHeader — the field-system section break.
   Pattern: a glowing LED-orange marker dot, then `// eyebrow` in mono lowercase,
   then a lowercase Helvetica heading. Optional description follows.

   Usage:
     <SectionHeader eyebrow="dashboard" title="Welcome back" />
     <SectionHeader eyebrow="pricing" title="Buy credits"
                    description="Credits let noctua read sources and prepare editions." />
     <SectionHeader eyebrow="rss feed" title="Subscribe" level="h2" size="md" />

   The marker is the same dot used in the design-system page and the landing
   page section labels — single source of truth for the "section break". */

type SectionHeaderSize = 'sm' | 'md' | 'lg' | 'xl';
type SectionHeaderLevel = 'h1' | 'h2' | 'h3';

interface SectionHeaderProps {
  /** The little mono label after `//`. Lowercased automatically. */
  eyebrow: string;
  /** Main heading text. Lowercased automatically via CSS. */
  title?: React.ReactNode;
  /** Optional supporting paragraph below the heading. */
  description?: React.ReactNode;
  /** Heading level — semantic, not visual. Defaults to h1. */
  level?: SectionHeaderLevel;
  /** Visual size for the heading. */
  size?: SectionHeaderSize;
  /** Hide the LED marker dot (rare — only when nesting inside an existing
      section that already carries one). */
  hideMarker?: boolean;
  className?: string;
}

const titleSizes: Record<SectionHeaderSize, string> = {
  sm: 'text-xl md:text-2xl',
  md: 'text-2xl md:text-3xl',
  lg: 'text-3xl md:text-4xl',
  xl: 'text-4xl md:text-5xl',
};

export default function SectionHeader({
  eyebrow,
  title,
  description,
  level = 'h1',
  size = 'xl',
  hideMarker = false,
  className = '',
}: SectionHeaderProps) {
  const HeadingTag = level;

  return (
    <header className={cn('mb-8', className)}>
      <p className="font-mono text-xs tracking-[0.18em] lowercase text-muted-foreground flex items-center gap-2.5 mb-3">
        {!hideMarker && (
          <span
            aria-hidden="true"
            className="inline-block w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]"
          />
        )}
        <span>{`// ${eyebrow.toLowerCase()}`}</span>
      </p>
      {title !== undefined && (
        <HeadingTag
          className={cn(
            'font-display font-bold lowercase tracking-[-0.03em] leading-[1.05] text-foreground',
            titleSizes[size]
          )}
        >
          {title}
        </HeadingTag>
      )}
      {description && (
        <p className="mt-4 max-w-2xl text-sm text-muted-foreground leading-relaxed">
          {description}
        </p>
      )}
    </header>
  );
}
